import React, { useEffect, useState } from 'react';
import { ErrorNote, Loading, PageHead, Panel, PanelHead } from '../components/aa';
import { pricingApi, type RateCard as RateCardData } from '../services/pricingApi';

const dollars = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

const perMillion = (value: number) => {
  if (value >= 1) return `$${value.toFixed(2)}`;
  return `$${value.toFixed(3)}`;
};

/* Public: what a company pays, in dollars, per model. The trust move is that
 * every number a customer is billed against is on this page. */
const RateCard: React.FC = () => {
  const [card, setCard] = useState<RateCardData | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    pricingApi.getRateCard().then(({ data }) => setCard(data)).catch(() => setFailed(true));
  }, []);

  if (failed) return <div className="mx-auto max-w-[1000px] px-4 py-10"><ErrorNote message="Could not load the rate card. Refresh to try again." /></div>;
  if (!card) return <Loading label="Loading the rate card" />;

  const models = [...card.models].sort((a, b) => a.provider.localeCompare(b.provider) || a.model.localeCompare(b.model));

  return (
    <div className="mx-auto max-w-[1000px] px-4 py-10">
      <PageHead
        eyebrow="Pricing"
        title="Rate card"
        sub="You pay per person, and agent usage is billed in plain dollars at the rates below. No credits, no hidden units."
      />

      <Panel className="mb-6">
        <PanelHead kicker="Seats" title={`${dollars(card.seat_price_cents)} per person, per month`} />
        <div className="px-6 pb-6 text-[13px] leading-[21.5px] text-ink-dim">
          Each seat includes {dollars(card.seat_allowance_cents)} of agent usage a month, pooled across your company and
          reset monthly. Usage past it comes out of your prepaid balance at the rates below, and work pauses rather than
          running up a bill. Runs funded by your own connected Claude seat cost $0 here.
        </div>
      </Panel>

      <Panel className="mb-6">
        <PanelHead kicker="Agent usage" title="Per model, per million tokens" />
        {!card.usage_rates_available && (
          <div className="px-6 pb-6 text-[13px] text-ink-dim">Model rates are being refreshed. Check back in a few minutes.</div>
        )}
        {card.usage_rates_available && (
          <div className="overflow-x-auto px-6 pb-6">
            <table className="w-full min-w-[560px] text-left text-[13px]">
              <thead className="text-ink-dim">
                <tr>
                  <th className="py-2 pr-4 font-semibold">Model</th>
                  <th className="py-2 pr-4 font-semibold">Input</th>
                  <th className="py-2 pr-4 font-semibold">Output</th>
                  <th className="py-2 pr-4 font-semibold">Cache read</th>
                  <th className="py-2 font-semibold">Cache write</th>
                </tr>
              </thead>
              <tbody className="text-ink">
                {models.map((m) => (
                  <tr key={`${m.provider}/${m.model}`} className="border-t border-line-soft">
                    <td className="py-2 pr-4">
                      <div className="font-semibold">{m.label || m.model}</div>
                      <div className="text-[12px] text-ink-dim">{m.provider}</div>
                    </td>
                    <td className="py-2 pr-4">{perMillion(m.input)}</td>
                    <td className="py-2 pr-4">{perMillion(m.output)}</td>
                    <td className="py-2 pr-4">{perMillion(m.cacheRead)}</td>
                    <td className="py-2">{perMillion(m.cacheWrite)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {card.compute && (
        <Panel>
          <PanelHead kicker="Compute" title={`$${card.compute.standardBoxPerHour.toFixed(2)} per workspace box hour`} />
          <div className="px-6 pb-6 text-[13px] leading-[21.5px] text-ink-dim">
            A standard workspace box has {card.compute.standardBox.vcpu} vCPUs and {card.compute.standardBox.memoryGib} GiB of
            memory, billed by the second while it runs.
          </div>
        </Panel>
      )}
    </div>
  );
};

export default RateCard;
